import { useState, useEffect, useCallback } from 'react';
import { Boxes, Box, Package, GitBranch, RefreshCw, AlertTriangle, Check, Plus, Pencil, Trash2, X, Save, type LucideIcon } from 'lucide-react';
import { apiService, PackGroupDTO, PackGroupInput, PackSummaryDTO } from '@/services/api.service';
import { clearPackTextureCache } from '@/services/packAssets';
import { clearConfigListCache } from '@/components/config/configListCache';
import { useGameStore } from '@/stores/game.store';
import { GAMES, getGameById, projectMatches } from '@/config/games';
import styles from './ConfigOverviewPage.module.scss';
import form from '@/components/config/PackForm.module.scss';

export function ConfigOverviewPage() {
  const activeGameId = useGameStore((s) => s.activeGameId);
  const game = getGameById(activeGameId);
  const [groups, setGroups] = useState<PackGroupDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<PackGroupInput | null>(null);

  const reload = useCallback(async () => {
    const res = await apiService.getPacksGroups();
    if (res.data) setGroups(res.data.groups);
    setLoading(false);
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const visible = groups.filter((g) => projectMatches(g.projects, activeGameId));

  const startCreate = () =>
    setEditing({
      id: '', name: '', type: 'block', color: game.accent.primary, projects: [activeGameId],
      behaviorRepo: '', resourceRepo: '', branch: 'main', behaviorBranch: '', resourceBranch: '', resourceReadonly: false,
    });

  const startEdit = (g: PackGroupDTO) =>
    setEditing({
      id: g.id, name: g.name, type: g.type, color: g.color || '#8B3FF1', projects: g.projects ?? [],
      behaviorRepo: g.behaviorRepo, resourceRepo: g.resourceRepo, branch: g.branch || 'main',
      behaviorBranch: g.behaviorBranch ?? '', resourceBranch: g.resourceBranch ?? '', resourceReadonly: !!g.resourceReadonly,
    });

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div>
          <h1 className={styles.title}>Configuration in-game — {game.name}</h1>
          <p className={styles.subtitle}>
            Groupes de packs (behavior &amp; resource) du projet {game.name}. {!loading && `${visible.length} groupe(s).`}
          </p>
        </div>
        {!editing && (
          <button className={styles.createBtn} style={{ backgroundColor: game.accent.primary }} onClick={startCreate}>
            <Plus size={16} /> Créer un groupe
          </button>
        )}
      </header>

      {editing && (
        <GroupForm
          initial={editing}
          isNew={!groups.some((g) => g.id === editing.id)}
          onCancel={() => setEditing(null)}
          onSaved={async () => { setEditing(null); await reload(); }}
        />
      )}

      {loading ? (
        <div className={styles.loadingState}><div className={styles.spinner} /><span>Chargement...</span></div>
      ) : visible.length === 0 && !editing ? (
        <div className={styles.warning}>
          <AlertTriangle size={16} />
          <span>Aucun groupe pour {game.name}. Clique sur « Créer un groupe ».</span>
        </div>
      ) : (
        <div className={styles.groupList}>
          {visible.map((g) => <GroupCard key={g.id} group={g} onEdit={() => startEdit(g)} onDeleted={reload} />)}
        </div>
      )}
    </div>
  );
}

function GroupForm({ initial, isNew, onCancel, onSaved }: { initial: PackGroupInput; isNew: boolean; onCancel: () => void; onSaved: () => void }) {
  const [g, setG] = useState<PackGroupInput>(initial);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const set = <K extends keyof PackGroupInput>(k: K, v: PackGroupInput[K]) => setG((s) => ({ ...s, [k]: v }));

  const toggleProject = (id: string) =>
    set('projects', g.projects.includes(id) ? g.projects.filter((p) => p !== id) : [...g.projects, id]);

  const save = async () => {
    if (!g.id.trim()) { setError('Identifiant requis'); return; }
    if (g.projects.length === 0) { setError('Sélectionne au moins un projet'); return; }
    setSaving(true);
    setError(null);
    const res = await apiService.savePackGroup(g);
    setSaving(false);
    if (res.data?.success) onSaved();
    else setError(res.data?.message || res.error || 'Échec');
  };

  return (
    <div className={styles.formCard}>
      <div className={styles.formHead}>
        <h3>{isNew ? 'Nouveau groupe' : `Éditer ${g.id}`}</h3>
        <button className={styles.iconBtn} onClick={onCancel} title="Fermer"><X size={16} /></button>
      </div>

      <div className={form.row2}>
        <div className={form.field}>
          <label className={form.label}>Identifiant (technique)</label>
          <input className={form.input} value={g.id} disabled={!isNew} onChange={(e) => set('id', e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, ''))} placeholder="ex. ngmod" />
        </div>
        <div className={form.field}>
          <label className={form.label}>Nom affiché</label>
          <input className={form.input} value={g.name} onChange={(e) => set('name', e.target.value)} placeholder="ex. NgMod" />
        </div>
      </div>

      <div className={form.row2}>
        <div className={form.field}>
          <label className={form.label}>Type (badge)</label>
          <input className={form.input} value={g.type} onChange={(e) => set('type', e.target.value)} placeholder="block, pack…" />
        </div>
        <div className={form.field}>
          <label className={form.label}>Couleur</label>
          <div className={styles.colorRow}>
            <input type="color" value={/^#[0-9a-fA-F]{6}$/.test(g.color) ? g.color : '#8B3FF1'} onChange={(e) => set('color', e.target.value)} />
            <input className={form.input} value={g.color} onChange={(e) => set('color', e.target.value)} placeholder="#8B3FF1" />
          </div>
        </div>
      </div>

      <div className={form.field}>
        <label className={form.label}>Projets</label>
        <div className={styles.projectChips}>
          {GAMES.map((p) => {
            const on = g.projects.includes(p.id);
            return (
              <button key={p.id} type="button" className={`${styles.projChip} ${on ? styles.projChipOn : ''}`}
                style={on ? { borderColor: p.accent.primary, backgroundColor: `${p.accent.primary}22`, color: p.accent.primary } : undefined}
                onClick={() => toggleProject(p.id)}>
                {on && <Check size={13} />} {p.name}
              </button>
            );
          })}
        </div>
      </div>

      <div className={form.row2}>
        <div className={form.field}>
          <label className={form.label}>Repo Behavior (URL git)</label>
          <input className={form.input} value={g.behaviorRepo} onChange={(e) => set('behaviorRepo', e.target.value)} placeholder="https://github.com/…" />
        </div>
        <div className={form.field}>
          <label className={form.label}>Repo Resource (URL git)</label>
          <input className={form.input} value={g.resourceRepo} onChange={(e) => set('resourceRepo', e.target.value)} placeholder="https://github.com/…" />
        </div>
      </div>

      <div className={form.row2}>
        <div className={form.field}>
          <label className={form.label}>Branche par défaut</label>
          <input className={form.input} value={g.branch} onChange={(e) => set('branch', e.target.value)} placeholder="main" />
        </div>
        <div className={form.field}>
          <label className={form.label}>Branche Resource (si différente)</label>
          <input className={form.input} value={g.resourceBranch} onChange={(e) => set('resourceBranch', e.target.value)} placeholder="(défaut : branche par défaut)" />
        </div>
      </div>

      <label className={form.checkRow}>
        <input type="checkbox" checked={g.resourceReadonly} onChange={(e) => set('resourceReadonly', e.target.checked)} />
        <span>Resource pack en lecture seule (repo externe, pas de publication)</span>
      </label>

      {error && <div className={form.error}><AlertTriangle size={14} /> {error}</div>}

      <div className={form.actions}>
        <button className={form.saveBtn} onClick={save} disabled={saving}>
          <Save size={14} /> {saving ? 'Enregistrement…' : 'Enregistrer'}
        </button>
        <button className={styles.cancelBtn} onClick={onCancel}>Annuler</button>
      </div>
    </div>
  );
}

function GroupCard({ group, onEdit, onDeleted }: { group: PackGroupDTO; onEdit: () => void; onDeleted: () => void }) {
  const [syncing, setSyncing] = useState(false);
  const [messages, setMessages] = useState<string[] | null>(null);
  const [packs, setPacks] = useState<PackSummaryDTO[] | null>(null);
  const [confirmDel, setConfirmDel] = useState(false);
  const accent = group.color || '#8B3FF1';

  useEffect(() => {
    apiService.getPacksScan(group.id).then((r) => setPacks(r.data?.packs ?? []));
  }, [group.id]);

  const sync = async () => {
    setSyncing(true);
    setMessages(null);
    const res = await apiService.syncPacksGroup(group.id);
    clearPackTextureCache(group.id);
    clearConfigListCache(group.id); // le contenu cloné a changé : on invalide le cache de listes
    setMessages(res.data?.messages ?? [res.error ?? 'Erreur de synchronisation']);
    const scan = await apiService.getPacksScan(group.id);
    setPacks(scan.data?.packs ?? []);
    setSyncing(false);
  };

  const del = async () => {
    await apiService.deletePackGroup(group.id);
    onDeleted();
  };

  return (
    <div className={styles.groupCard} style={{ borderLeftColor: accent }}>
      <div className={styles.groupHead}>
        <span className={styles.groupBadge} style={{ backgroundColor: accent }}>{group.type || 'pack'}</span>
        <div className={styles.groupTitleBlock}>
          <h3 className={styles.groupName}>{group.name || group.id}</h3>
          <span className={styles.groupId}>{group.id} · {(group.projects ?? []).map((p) => getGameById(p).name).join(', ')}</span>
        </div>
        <div className={styles.groupActions}>
          <button className={styles.iconBtn} onClick={onEdit} title="Éditer"><Pencil size={15} /></button>
          {confirmDel ? (
            <>
              <button className={styles.dangerBtn} onClick={del}>Confirmer</button>
              <button className={styles.iconBtn} onClick={() => setConfirmDel(false)}><X size={15} /></button>
            </>
          ) : (
            <button className={styles.iconBtn} onClick={() => setConfirmDel(true)} title="Supprimer"><Trash2 size={15} /></button>
          )}
          <button className={styles.syncButton} onClick={sync} disabled={syncing} style={{ backgroundColor: accent }}>
            <RefreshCw size={16} strokeWidth={2} className={syncing ? styles.spinning : ''} />
            {syncing ? 'Sync...' : 'Synchroniser'}
          </button>
        </div>
      </div>

      <div className={styles.repos}>
        <RepoRow icon={Box} label="Behavior" url={group.behaviorRepo} branch={group.behaviorBranch || group.branch} />
        <RepoRow icon={Package} label="Resource" url={group.resourceRepo} branch={group.resourceBranch || group.branch} />
      </div>

      {messages && (
        <ul className={styles.syncMessages}>
          {messages.map((m, i) => (<li key={i}><Check size={12} /> {m}</li>))}
        </ul>
      )}

      {packs !== null && (
        <div className={styles.packsSummary}>
          <Boxes size={14} />
          {packs.length === 0
            ? <span>Aucun pack (synchronise le groupe pour cloner les repos).</span>
            : <span>{packs.length} pack(s) : {packs.map((p) => p.name || p.path).join(', ')}</span>}
        </div>
      )}
    </div>
  );
}

function RepoRow({ icon: Icon, label, url, branch }: { icon: LucideIcon; label: string; url: string; branch: string }) {
  return (
    <div className={styles.repoRow}>
      <Icon size={16} className={styles.repoIcon} />
      <span className={styles.repoLabel}>{label}</span>
      {url ? (
        <a className={styles.repoUrl} href={url} target="_blank" rel="noreferrer">{url.replace(/^https?:\/\//, '')}</a>
      ) : (
        <span className={styles.repoEmpty}>non configuré</span>
      )}
      {url && branch && <span className={styles.repoBranch}><GitBranch size={12} /> {branch}</span>}
    </div>
  );
}

export function ConfigPlaceholder({ title, icon: Icon }: { title: string; icon: LucideIcon }) {
  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <h1 className={styles.title}>{title}</h1>
      </header>
      <div className={styles.comingSoon}>
        <Icon size={48} strokeWidth={1.5} />
        <h3>{title}</h3>
        <p>Édition en cours de portage — bientôt disponible.</p>
      </div>
    </div>
  );
}
